import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LockIcon, PlugZapIcon } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { bundleName, formatMoney, getBundle, priceLine } from '../../utils/pricing';
import { buildCartPermalink } from '../../utils/shopify';
import { track } from '../../utils/analytics';
import { buttonClasses } from '../../utils/buttonStyles';
export function Checkout() {
  const {
    lines,
    totals
  } = useCart();
  const {
    lang
  } = useLanguage();
  const m = (n: number) => formatMoney(n, lang);
  const permalink = buildCartPermalink(lines);
  useEffect(() => {
    if (!lines.length) return;
    const contents = lines.map(l => {
      const p = priceLine(l);
      return l.kind === 'system' ? {
        id: getBundle(l.bundleId).sku,
        quantity: 1,
        item_price: p.total
      } : {
        id: 'MFL-M1PRO-1',
        quantity: l.qty,
        item_price: p.total / l.qty
      };
    });
    track('begin_checkout', {
      value: totals.total,
      currency: 'USD',
      content_ids: contents.map(c => c.id),
      content_type: 'product',
      contents,
      num_items: totals.lights
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!lines.length) {
    return <div className="mx-auto max-w-site px-5 py-20 md:px-8" data-id="element-1340" data-mp="2057qq4l950c8">
        <h1 className="font-display text-5xl font-semibold uppercase" data-id="element-1341" data-mp="2dxdwqxuoxqxy">Checkout</h1>
        <p className="mt-4 text-titanium" data-id="element-1342" data-mp="2dziccd1ycee2">Your cart is empty.</p>
        <Link to="/shop" className={`${buttonClasses('primary')} mt-6`} data-id="element-1343" data-mp="29viwzxserlma">
          Build your system
        </Link>
      </div>;
  }
  return <div className="mx-auto grid max-w-site gap-12 px-5 py-14 md:px-8 md:py-20 lg:grid-cols-[1fr_420px]" data-id="element-1344" data-mp="2iwqs66zmo6c5">
      <div data-id="element-1345" data-mp="thkbqmy4v5vn">
        <h1 className="font-display text-5xl font-semibold uppercase md:text-6xl" data-id="element-1346" data-mp="th0uz9k7ynj7">Secure Checkout</h1>
        <p className="mt-4 max-w-xl text-titanium" data-id="element-1347" data-mp="ojiwye7qpspz">
          Your order is handed to our secure Shopify checkout, where shipping, taxes and payment are completed.
        </p>

        {permalink.url ? <a href={permalink.url} onClick={() => track('checkout_handoff', {
        value: totals.total
      })} className={`${buttonClasses('primary', 'lg')} mt-10`} data-id="element-1348" data-mp="2tl3dki1bvkcs">
            <LockIcon className="h-4 w-4" data-id="element-1349" data-mp="32lt4b7bqekjn" /> Continue to Shopify checkout
          </a> : <div className="mt-10 rounded-2xl border border-ice/25 bg-ice/[0.05] p-6 md:p-8" data-id="element-1350" data-mp="37is1xzult9ml">
            <div className="flex items-center gap-3" data-id="element-1351" data-mp="3chw6ueahwl74">
              <PlugZapIcon className="h-5 w-5 text-ice" aria-hidden data-id="element-1352" data-mp="3cgl50k0m1nin" />
              <h2 className="font-display text-2xl font-semibold uppercase" data-id="element-1353" data-mp="3ha6289udqdgs">Secure checkout opening soon</h2>
            </div>
            <p className="mt-3 text-titanium" data-id="element-1354" data-mp="3ld2frm52bjdv">
              Online checkout is launching shortly. Your cart is saved on this device — contact our team if you'd like help
              with your order.
            </p>
            <button type="button" disabled className={`${buttonClasses('primary', 'lg')} mt-6`} data-id="element-1355" data-mp="97oq3yn29gdg">
              <LockIcon className="h-4 w-4" data-id="element-1356" data-mp="4951fqh31qhe" /> Continue to Shopify checkout
            </button>
          </div>}

        <Link to="/cart" className="mt-6 inline-block text-sm text-titanium hover:text-white" data-id="element-1357" data-mp="3veel70ehh8ik">
          ← Return to cart
        </Link>
      </div>

      <aside aria-label="Order summary" className="rounded-2xl border mf-hairline bg-navy p-6 md:p-8 lg:self-start" data-id="element-1358" data-mp="3rfnxfjvwr384">
        <h2 className="font-display text-2xl font-semibold uppercase" data-id="element-1359" data-mp="mzcz3t20joq7">Your order</h2>
        <ul className="mt-5 divide-y divide-white/10" data-id="element-1360" data-mp="n1fwwz9eqm5h">
          {lines.map(l => {
          const p = priceLine(l);
          const name = l.kind === 'system' ? bundleName(getBundle(l.bundleId), lang) : 'M1 PRO — Build Your Own';
          return <li key={l.id} className="flex justify-between gap-4 py-4 text-sm" data-id="element-1361" data-mp="1apqvfvrms9ql">
                <span data-id="element-1362" data-mp="15xyyn3hvt7s0">
                  <span className="block text-white" data-id="element-1363" data-mp="15wnyrerk4k66">{name}</span>
                  <span className="text-titanium" data-id="element-1364" data-mp="qfo7ecgkqh33">
                    {p.lights} lights{l.kind === 'system' && l.extras ? ` (incl. ${l.extras} extra)` : ''}
                  </span>
                </span>
                <span className="text-white" data-id="element-1365" data-mp="hgazvpc0g2l0">{m(p.total)}</span>
              </li>;
        })}
        </ul>
        <div className="mt-4 flex items-baseline justify-between border-t mf-hairline pt-4" data-id="element-1366" data-mp="lhw8uzlfbwpx">
          <span className="text-titanium" data-id="element-1367" data-mp="1980a50b53kdr">Subtotal</span>
          <span className="font-display text-3xl font-semibold" data-id="element-1368" data-mp="1e5sa8hlxuct1">{m(totals.total)}</span>
        </div>
        <p className="mt-2 text-xs text-titanium/70" data-id="element-1369" data-mp="10en91b2x9cgl">Shipping and taxes calculated by Shopify at checkout.</p>
        <p className="mt-5 border-t mf-hairline pt-4 text-sm text-titanium" data-id="element-1370" data-mp="14fgwk3cgcp8y">
          Professional-grade lighting, backed by professional-grade support — the MarineForce Promise.
        </p>
      </aside>
    </div>;
}